
import { Fragment } from "react";
import { useStytchMember } from "@stytch/react/b2b";
import moment from "moment-timezone";
import { useCreateKnowledgeReport, fetchKnowledgeItemsPage, KnowledgeApi, useGetCommonBlocks, useGetKnowledgeTags, useKnowledgeItemsPaginated } from "@/lib/api/knowledge";
import { cn, CommonDestinationTimezone, GenerateLoadingOrError } from "@/lib/utils";
import { ArrowLeft, DownloadCloud, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useListGrainSamplesDownloadedBatches, useDownloadLatestSamples, useDownloadSampleBatch, useListGrainSamplesToDownloadBreakdown, useListGrainSamplesActive, useAttachCustomerTo } from "@/lib/api/grainSamples";
import { useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { Loader } from "@/components/ui/loader";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { downloadFromS3 } from "@/lib/file";
import { Separator } from "@/components/ui/separator";
import { PermissionType, PossiblePackage } from "@lhc/permissions/stytch_original";
import { hasAccessToFeature } from "@/lib/models/auth";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useListCustomers } from "@/lib/api/customer";
import { Combobox } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";

export function GrainSamplesDashboard() {
    const { member } = useStytchMember();
    const navigate = useNavigate();
    const [tab, onTabChange] = useState<"summary" | "batches">("summary");

    return (
        <section className="flex min-h-full flex-1 flex-col justify-start px-6 lg:px-8">
            <div className="flex items-center justify-between mb-6">
                <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
                    Grain Samples
                </h1>
            </div>

            <Tabs value={tab} onValueChange={(item) => onTabChange(item as any)} className="space-y-6">
                <TabsList>
                    <TabsTrigger value="summary">New Samples</TabsTrigger>
                    <TabsTrigger value="batches">Historical Batches</TabsTrigger>
                </TabsList>

                <TabsContent value="summary" className="space-y-4">
                    <SummaryView />
                </TabsContent>

                <TabsContent value="batches" className="space-y-4">
                    <BatchesView />
                </TabsContent>
            </Tabs>
        </section>
    );
}

function SummaryView() {
    const { toast } = useToast();
    const [isSaving, setIsSaving] = useState(false);
    const [isAttaching, setIsAttaching] = useState(false);

    const [link, setLink] = useState<null | string>(null);
    const [customerId, setCustomerId] = useState<null | string>(null);
    const summaryRes = useListGrainSamplesToDownloadBreakdown();
    const samplesRes = useListGrainSamplesActive();
    const customersResult = useListCustomers({ minimal: true });
    const downloadLatestSamples = useDownloadLatestSamples();
    const attachCustomerTo = useAttachCustomerTo();

    const summary = (summaryRes?.data?.data || {});
    const breakdownRows = useMemo(() => {
        const location_names = Object.keys(summary.summary || {});
        if (!location_names?.length) return [];
        location_names.sort();

        return location_names.map((location_name: string) => ({ location_name, count: summary.summary[location_name] || 0 }));
    }, [summary]);

    const customerOptions = useMemo(() => {
        const customers = customersResult?.data?.data || [];
        return customers.map((customer: any) => ({ value: customer.id, label: customer.long_name }))
    }, [customersResult])


    const loadingOrErrorUi = GenerateLoadingOrError([summaryRes, customersResult, samplesRes].filter(Boolean));

    if (loadingOrErrorUi) {
        return (loadingOrErrorUi);
    }

    const onSave = async () => {
        try {
            if (isSaving) { return; }
            setIsSaving(true);

            const result = await downloadLatestSamples();
            if (!result.success) {
                throw result;
            }

            if (!result.data.link) {
                toast({
                    title: "Found no new samples",
                    description: "",
                });
                setLink(null);
                return;
            }

            const currentDate = moment.tz(CommonDestinationTimezone).format("YYYY-MM-DD_HH:mm");
            setLink(result.data.link);
            downloadFromS3(result.data.link, `latest-grain-samples-${currentDate}.csv`);

            toast({
                title: "Generated",
                description: "",
            });
        } catch (error) {
            console.error("Failed to download Grain Sample Links", error);
            toast({
                title: "Failed to download latest Grain Samples",
                description: "",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const activeSamples: any[] = samplesRes?.data?.data || [];;
    const onAssign = async (id: string) => {
        try {
            if (isAttaching) { return; }
            if (!customerId) { return; }
            setIsAttaching(true);

            const result = await attachCustomerTo({ id, customer_id: customerId! });
            if (!result.success) {
                throw result;
            }

            toast({
                title: "Assigned",
                description: "",
            });
        } catch (error) {
            console.error("Failed to assign grain sample customer", error);
            toast({
                title: "Failed to assign producer",
                description: "",
            });
        } finally {
            setIsAttaching(false);
        }

    };

    return (
        <div className="flex flex-col justify-center items-start gap-4">
            <div className="flex flex-col gap-4 md:grid md:grid-cols-4">
                <div className="flex flex-col lg:flex-row lg:justify-between items-center">
                    <Card className="min-w-[400px]">
                        <CardHeader>
                            <CardTitle>
                                Download Latest Samples Report
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Button onClick={onSave} disabled={isSaving}>
                                {isSaving ? "Generating..." : "Generate Report"}
                            </Button>
                            {link && (
                                <div className="flex flex-col gap-1 my-2">
                                    <Separator className="my-2" />
                                    <p className="text-sm text-muted-foreground">
                                        Report generated successfully.
                                    </p>
                                    <a href={link} target="_blank" rel="noopener noreferrer" className="mt-2 text-xs text-muted-foreground underline decoration-solid decoration-sky-500">Click here to view report, if it doesn't download automatically.</a>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>



                <Card className="w-[400px] ">
                    <CardHeader className="pb-2">
                        <CardTitle >Total Count</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex justify-between items-center text-sm">
                            <span className="tabular-nums text-lg font-bold">{summary.count}</span>
                        </div>
                    </CardContent>
                </Card>
            <Card className="m-w-[400px]">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">Count Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table className="compact-table">
                        <TableHeader>
                            <TableRow className="h-8">
                                <TableHead className="py-1">Location Name</TableHead>
                                <TableHead className="py-1 text-right">Count</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {breakdownRows.map((row: any) => {
                                return (
                                    <Fragment key={`breakdown-row-${row.location_name}`}>
                                        <TableRow className="h-7 text-sm bg-gray-200 dark:bg-gray-700">
                                            <TableCell className="py-1 font-semibold uppercase">{row.location_name}</TableCell>
                                            <TableCell className="py-1 text-right tabular-nums font-semibold">{row.count}</TableCell>
                                        </TableRow>
                                    </Fragment>
                                );
                            })}
                            {!breakdownRows.length && (
                                <TableRow>
                                    <TableCell colSpan={2} className="h-24 text-center">
                                        No data available.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
            </div>



<div className="mt-4"></div>
<div className="flex flex-col gap-2">
<Label htmlFor="assignCustomerId" className="text-lg text-bold">Producer</Label>
<p className="text-muted-foreground text-sm">Pick a Producer and click Assign button to assign a Producer to a Grain Sample</p>
<Combobox
dwimOnChange
className="max-w-[400px] min-w-[250px]"
popoverClassName="w-[400px] h-[200px]"

virtualizeList
id="assignCustomerId"
options={customerOptions}
value={customerId || ""}
onValueChange={setCustomerId}
/>
</div>

                    <Table className="compact-table">
                        <TableHeader>
                            <TableRow className="h-8">
                                <TableHead className="py-1 text-center">Assign</TableHead>
                                <TableHead className="py-1 text-start">Client</TableHead>
                                <TableHead className="py-1 text-start">Date</TableHead>
                                <TableHead className="py-1 text-center">ID</TableHead>
                                <TableHead className="py-1 text-left">Commodity</TableHead>
                                <TableHead className="py-1 text-start">DKG</TableHead>
                                <TableHead className="py-1 text-start">TW</TableHead>
                                <TableHead className="py-1 text-start">M</TableHead>
                                <TableHead className="py-1 text-start">DKT</TableHead>
                                <TableHead className="py-1 text-start">FM</TableHead>
                                <TableHead className="py-1 text-start">SHBM</TableHead>
                                <TableHead className="py-1 text-start">DEF</TableHead>
                                <TableHead className="py-1 text-start">DHV</TableHead>
                                <TableHead className="py-1 text-start">PRO</TableHead>
                                <TableHead className="py-1 text-start">DON</TableHead>
                                <TableHead className="py-1 text-start">ERG</TableHead>
                                <TableHead className="py-1 text-start">FN</TableHead>
                                <TableHead className="py-1 text-left">Remarks</TableHead>
                                <TableHead className="py-1 text-center">Location</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {activeSamples.map((row: any, index: number) => {
                                return (
                                    <Fragment key={`breakdown-row-${row.id}`}>
                                        <TableRow className={cn("h-7 text-sm", index % 2 === 0 ? "bg-gray-200 dark:bg-gray-700" : "bg-gray-300 dark:bg-gray-600")}>
                                            <TableCell className="py-1 font-semibold uppercase"><Button variant="outline" size="sm" onClick={() => onAssign(row.id)} disabled={!customerId || isAttaching}>{isAttaching ? "Updating" : "Assign"}</Button></TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.client_name}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.date ? moment.tz(row.date, CommonDestinationTimezone).format("MM/DD/YYYY hh:mm a") : ''}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.sample_id}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.commodity}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.grades["DKG"]?.toString() || ""}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.grades["TW"]?.toString() || ""}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.grades["M"]?.toString() || ""}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.grades["DKT"]?.toString() || ""}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.grades["FM"]?.toString() || ""}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.grades["SHBN"]?.toString() || ""}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.grades["DEF"]?.toString() || ""}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.grades["DHV"]?.toString() || ""}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.grades["PROT"]?.toString() || ""}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.grades["DON"]?.toString() || ""}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.grades["ERG"]?.toString() || ""}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.grades["FN"]?.toString() || ""}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.remarks || ""}</TableCell>
                                            <TableCell className="py-1 font-semibold uppercase">{row.location}</TableCell>
                                        </TableRow>
                                    </Fragment>
                                );
                            })}
                            {!activeSamples.length && (
                                <TableRow>
                                    <TableCell colSpan={2} className="h-24 text-center">
                                        No data available.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
        </div>
    );
}

function BatchesView() {
    const { toast } = useToast();
    const [isSaving, setIsSaving] = useState(false);
    const [link, setLink] = useState<null | string>(null);
    const { member } = useStytchMember();
    const batchesRes = useListGrainSamplesDownloadedBatches();
    const downloadLatestBatch = useDownloadSampleBatch();

    const batches = (batchesRes?.data?.data || []);
    const user_id = member?.trusted_metadata.db_user_id;
    const member_role = member?.trusted_metadata.role;
    const isAdmin = member_role === "admin";

    const loadingOrErrorUi = GenerateLoadingOrError([batchesRes].filter(Boolean));

    if (loadingOrErrorUi) {
        return (loadingOrErrorUi);
    }

    const onSave = async (batchId: string, historicalDate: Date | string) => {
        try {
            if (isSaving) { return; }
            if (!batchId) { return; }
            setIsSaving(true);

            const result = await downloadLatestBatch(batchId);
            if (!result.success) {
                throw result;
            }

            const historicalFormattedDate = moment.tz(historicalDate, CommonDestinationTimezone).format("YYYY-MM-DD_HH:mm");
            setLink(result.data.link);
            downloadFromS3(result.data.link, `historical-grain-samples-${historicalFormattedDate}.csv`);

            toast({
                title: "Generated",
                description: "",
            });
        } catch (error) {
            console.error("Failed to download Grain Sample historical batch", error);
            toast({
                title: "Failed to download historical batch",
                description: "",
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex flex-col justify-center items-start gap-4">
            {link && (
                <div className="flex flex-col lg:flex-row lg:justify-between items-center">
                    <Card className="min-w-[400px]">
                        <CardHeader>
                            <CardTitle>
                                Generated Batch Re-Download
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {link && (
                                <div className="flex flex-col gap-1 my-2">
                                    <a href={link} target="_blank" rel="noopener noreferrer" className="mt-2 text-xs text-muted-foreground underline decoration-solid decoration-sky-500">Click here to view report, if it doesn't download automatically.</a>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}


            <Card className="m-w-[400px]">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">Historical Batch Downloads</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table className="compact-table">
                        <TableHeader>
                            <TableRow className="h-8">
                                {isAdmin && (<TableHead className="py-1">User</TableHead>)}
                                <TableHead className="py-1 text-right">Downloaded Date</TableHead>
                                <TableHead className="py-1 text-right">Count</TableHead>
                                <TableHead className="py-1 text-right">Download</TableHead>
                                <TableHead className="py-1 text-right">Is Historical Batch Download</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {batches.map((row: any) => {
                                return (
                                    <TableRow className="h-7 text-sm bg-gray-200 dark:bg-gray-700" key={`breakdown-row-${row.id}`}>
                                        {isAdmin && (<TableCell className="py-1 ">{row.User?.name}</TableCell>)}
                                        <TableCell className="py-1  ">{moment.tz(row.downloaded_date, CommonDestinationTimezone).format("MM/DD/YYYY hh:mm a")}</TableCell>
                                        <TableCell className="py-1 font-semibold uppercase">{row.ticket_count}</TableCell>
                                        <TableCell className="py-1 text-right tabular-nums ">
                                            <Button size="sm" variant={"ghost"} onClick={() => onSave(row.id, row.downloaded_date)}>
                                                <DownloadCloud className="w-4 h-4" />
                                            </Button>
                                        </TableCell>
                                        <TableCell className="py-1 ">{row.is_historical_download ? "Historical" : "Original"}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
}
